import { useCallback } from 'react';
import { useAuth } from 'contexts/AuthContext';
import { useAzureAuth } from 'contexts/AzureAuthContext';
import { AuthService } from 'services/AuthService';
import GoogleAuthService from 'services/GoogleAuthService';
import { getRuntimeConfig } from 'runtime/runtimeConfig';

/**
 * The one sign-out: the admin header, the portal header and the
 * portal-moved notice all call this.
 *
 * Clearing the local session is not enough while an EasyAuth platform session
 * is alive: `/azure-auth` (probed on every load by `AzureAuthProvider`) and
 * `/google-auth` (probed by `LoginForm`) would mint the same user's token
 * again. So the platform sign-out (`/.auth/logout`, a browser redirect that
 * clears every provider) runs whenever either provider can be live on this
 * origin. A password-only origin just clears the token and the login form
 * renders.
 */
export function useSignOut(): () => void {
  const { logout } = useAuth();
  const { isEnabled: azureEnabled, logoutFromAzure } = useAzureAuth();

  return useCallback(() => {
    // Read before `logout()` deletes the record. A Google-minted session
    // proves the binding exists even when /runtime-config does not say so
    // (older backend: `google_enabled` is null).
    const googleSession = AuthService.getSessionProvider() === 'google';
    logout();
    if (azureEnabled) {
      logoutFromAzure();
    } else if (googleSession || getRuntimeConfig().auth.google_enabled === true) {
      GoogleAuthService.logoutFromGoogle();
    }
  }, [logout, azureEnabled, logoutFromAzure]);
}
